import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Shield, Lock, FileText, Mail, AlertCircle, CheckCircle2 } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      {/* Header */}
      <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
          <div className="flex gap-2">
            <Link href="/terms">
              <Button variant="outline" size="sm">
                Terms of Use
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <img src="/logo.png" alt="Tradeharbor.io" className="h-20 w-auto" />
          </div>
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-lg text-muted-foreground">
            Last Updated: February 10, 2026
          </p>
        </div>

        {/* Quick Links Card */}
        <Card className="mb-8">
          <CardContent className="py-4">
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <a href="#info-collect" className="flex items-center gap-2 hover:text-primary transition-colors">
                <FileText className="h-4 w-4" />
                Information We Collect
              </a>
              <a href="#how-we-use" className="flex items-center gap-2 hover:text-primary transition-colors">
                <Lock className="h-4 w-4" />
                How We Use Data
              </a>
              <a href="#data-sharing" className="flex items-center gap-2 hover:text-primary transition-colors">
                <Shield className="h-4 w-4" />
                Data Sharing
              </a>
              <a href="#ai-data" className="flex items-center gap-2 hover:text-primary transition-colors">
                <AlertCircle className="h-4 w-4" />
                AI Data Usage
              </a>
              <a href="#your-rights" className="flex items-center gap-2 hover:text-primary transition-colors">
                <CheckCircle2 className="h-4 w-4" />
                Your Rights
              </a>
              <a href="#contact" className="flex items-center gap-2 hover:text-primary transition-colors">
                <Mail className="h-4 w-4" />
                Contact Us
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Main Content */}
        <div className="prose prose-slate dark:prose-invert max-w-none space-y-8">
          
          <section>
            <h2>Introduction</h2>
            <p>
              Tradeharbor.io ("we", "us", "our", "Platform") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your personal 
              information when you use our financial technology platform.
            </p>
            <p>This Privacy Policy complies with:</p>
            <ul>
              <li>Gramm-Leach-Bliley Act (GLBA)</li>
              <li>SEC Regulation S-P</li>
              <li>California Consumer Privacy Act (CCPA)</li>
              <li>Other applicable privacy laws and regulations</li>
            </ul>
            <Card className="border-yellow-500/50 bg-yellow-500/10 my-4">
              <CardContent className="py-4">
                <p className="text-sm font-semibold m-0">
                  BY USING TRADEHARBOR.IO, YOU CONSENT TO THE COLLECTION, USE, AND DISCLOSURE OF YOUR 
                  INFORMATION AS DESCRIBED IN THIS PRIVACY POLICY.
                </p>
              </CardContent>
            </Card>
          </section>

          <section id="info-collect">
            <h2>1. Information We Collect</h2>
            
            <h3>1.1 Personal Information You Provide</h3>
            <p>When you create an account or use our services, we collect:</p>
            
            <h4>Account Registration Information:</h4>
            <ul>
              <li>Full legal name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Date of birth</li>
              <li>Social Security Number or Tax ID</li>
              <li>Physical address</li>
            </ul>

            <h4>Financial Information:</h4>
            <ul>
              <li>Bank account information (for funding)</li>
              <li>Income and net worth</li>
              <li>Investment experience and objectives</li>
              <li>Employment information</li>
            </ul>

            <h3>1.2 Information We Collect Automatically</h3>
            <ul>
              <li>Securities transactions and order history</li>
              <li>Portfolio positions and balances</li>
              <li>Platform usage data and analytics</li>
              <li>Device and browser information</li>
              <li>IP address and location data</li>
            </ul>
          </section>

          <section id="how-we-use">
            <h2>2. How We Use Your Information</h2>
            
            <h3>2.1 To Provide Services</h3>
            <p>We use your information to:</p>
            <ul>
              <li>Create and maintain your account</li>
              <li>Facilitate trading through Alpaca Markets</li>
              <li>Process deposits and withdrawals</li>
              <li>Provide AI-powered sentiment analysis</li>
              <li>Generate tax documents and reports</li>
            </ul>

            <h3>2.2 For Security and Compliance</h3>
            <p>We use your information to:</p>
            <ul>
              <li>Verify your identity and prevent fraud</li>
              <li>Comply with KYC/AML requirements</li>
              <li>Meet regulatory reporting obligations</li>
              <li>Detect and prevent unauthorized access</li>
            </ul>
          </section>

          <section id="data-sharing">
            <h2>3. How We Share Your Information</h2>
            
            <Card className="border-green-500/50 bg-green-500/10 my-6">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <Shield className="h-6 w-6 text-green-600" />
                  <div>
                    <p className="font-bold text-lg mb-2">WE DO NOT SELL YOUR PERSONAL INFORMATION</p>
                    <p className="text-sm m-0">
                      Tradeharbor.io does NOT sell, rent, or trade your personal information to third 
                      parties for marketing purposes.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <h3>3.1 Alpaca Markets (Broker-Dealer)</h3>
            <p>We share with Alpaca Markets:</p>
            <ul>
              <li>✓ Account and identity information</li>
              <li>✓ Trading orders and instructions</li>
              <li>✓ Portfolio and balance data</li>
              <li>✓ All information necessary for brokerage services</li>
            </ul>
            <p className="text-sm text-muted-foreground">
              Alpaca Markets is your registered broker-dealer (Member FINRA/SIPC) and requires this 
              information to provide brokerage services.
            </p>
          </section>

          <section id="ai-data">
            <h2>3.2 Anthropic (Claude AI) - CRITICAL PRIVACY INFORMATION</h2>
            
            <Card className="border-blue-500/50 bg-blue-500/10 my-6">
              <CardContent className="py-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-6 w-6 text-blue-600 mt-1" />
                  <div>
                    <p className="font-bold text-lg mb-3">What We Share with AI (Claude by Anthropic):</p>
                    
                    <div className="space-y-4">
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                        <p className="font-semibold text-green-700 dark:text-green-400 mb-2">✓ What IS Shared:</p>
                        <ul className="text-sm space-y-1 m-0">
                          <li>• News articles and market headlines</li>
                          <li>• Public market data and analysis text</li>
                          <li>• Stock symbols and ticker information</li>
                        </ul>
                      </div>

                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                        <p className="font-semibold text-red-700 dark:text-red-400 mb-2">✗ What is NOT Shared:</p>
                        <ul className="text-sm space-y-1 m-0">
                          <li>• ✗ Personal identifiable information (name, email, SSN)</li>
                          <li>• ✗ Account balances or portfolio positions</li>
                          <li>• ✗ Trading orders or transaction history</li>
                          <li>• ✗ Financial information or bank details</li>
                          <li>• ✗ Any information that identifies you personally</li>
                        </ul>
                      </div>

                      <p className="text-sm italic m-0">
                        Your private financial data never leaves the secure Tradeharbor-Alpaca connection. 
                        The AI only sees generic market news to generate sentiment scores.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <h3>3.3 Other Service Providers</h3>
            <p>We share limited information with:</p>
            <ul>
              <li>Cloudflare (hosting and security)</li>
              <li>Analytics providers (aggregated usage data only)</li>
              <li>Customer support platforms</li>
              <li>Email and communication services</li>
            </ul>
          </section>

          <section>
            <h2>4. Data Security</h2>
            
            <h3>4.1 Security Measures</h3>
            <p>We implement industry-standard security measures:</p>
            
            <h4>Technical Safeguards:</h4>
            <ul>
              <li>End-to-end encryption for data transmission (TLS/SSL)</li>
              <li>Encryption of sensitive data at rest (AES-256)</li>
              <li>Secure password hashing (PBKDF2)</li>
              <li>Multi-factor authentication (MFA) options</li>
              <li>Regular security audits and penetration testing</li>
            </ul>

            <h4>Administrative Safeguards:</h4>
            <ul>
              <li>Employee background checks and training</li>
              <li>Strict access controls and least-privilege policies</li>
              <li>Regular security awareness training</li>
              <li>Incident response plans</li>
            </ul>

            <h3>4.2 Your Responsibilities</h3>
            <p>You are responsible for:</p>
            <ul>
              <li>Keeping your login credentials confidential</li>
              <li>Using strong, unique passwords</li>
              <li>Enabling multi-factor authentication</li>
              <li>Monitoring your account for unauthorized activity</li>
            </ul>
          </section>

          <section>
            <h2>5. Data Retention</h2>
            <p>We retain your information for:</p>
            <ul>
              <li>As long as your account is active</li>
              <li>7 years after account closure (regulatory requirement)</li>
              <li>6 years for trading records (SEC requirement)</li>
              <li>3 years for communications records</li>
            </ul>
          </section>

          <section id="your-rights">
            <h2>6. Your Privacy Rights</h2>
            
            <h3>6.1 Access and Correction</h3>
            <p>You have the right to:</p>
            <ul>
              <li>Access your personal information</li>
              <li>Request corrections to inaccurate data</li>
              <li>Download a copy of your data</li>
            </ul>

            <h3>6.2 California Residents (CCPA Rights)</h3>
            <p>California residents have additional rights:</p>
            <ul>
              <li>Right to know what information we collect</li>
              <li>Right to delete personal information</li>
              <li>Right to opt-out of data sales (we don't sell data)</li>
              <li>Right to non-discrimination for exercising rights</li>
            </ul>

            <h3>6.3 Exercising Your Rights</h3>
            <p>
              To exercise these rights, contact us at: <a href="mailto:privacy@tradeharbor.io" className="text-primary hover:underline">privacy@tradeharbor.io</a>
            </p>
          </section>

          <section>
            <h2>7. Cookies and Tracking</h2>
            <p>We use cookies for:</p>
            <ul>
              <li>Essential platform functionality</li>
              <li>Analytics and performance monitoring</li>
              <li>User preferences and settings</li>
            </ul>
            <p>You can control cookies through your browser settings.</p>
          </section>

          <section>
            <h2>8. Updates to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Material changes will be communicated 
              via email or platform notification at least 30 days before taking effect.
            </p>
          </section>

          <section id="contact">
            <h2>9. Contact Information</h2>
            
            <div className="not-prose">
              <Card>
                <CardContent className="py-4 space-y-3">
                  <div>
                    <p className="font-semibold flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Privacy Questions
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Email: <a href="mailto:privacy@tradeharbor.io" className="text-primary hover:underline">privacy@tradeharbor.io</a>
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Support: <a href="mailto:support@tradeharbor.io" className="text-primary hover:underline">support@tradeharbor.io</a>
                    </p>
                  </div>

                  <div>
                    <p className="font-semibold flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Regulatory Information
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Securities & Exchange Commission (SEC): (202) 551-6551<br />
                      FINRA Investor Complaint Line: (301) 590-6500<br />
                      SIPC: <a href="https://www.sipc.org" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">www.sipc.org</a>
                    </p>
                  </div>

                  <div>
                    <p className="font-semibold">Brokerage Services</p>
                    <p className="text-sm text-muted-foreground">
                      Provided by <strong>Alpaca Markets, Inc.</strong> - Member FINRA/SIPC<br />
                      <a href="https://alpaca.markets" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        https://alpaca.markets
                      </a>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          <Card className="border-blue-500/50 bg-blue-500/10 my-8">
            <CardContent className="py-6">
              <p className="text-center font-semibold mb-4">
                Your privacy is our priority. We are committed to protecting your personal and financial information.
              </p>
              <p className="text-sm text-center m-0">
                For detailed information about how we protect your data, please review this complete policy 
                or contact our privacy team at <a href="mailto:privacy@tradeharbor.io" className="text-primary hover:underline">privacy@tradeharbor.io</a>
              </p>
            </CardContent>
          </Card>

          <p className="text-sm text-muted-foreground text-center italic">
            This Privacy Policy is effective as of February 10, 2026 and supersedes all prior versions.
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 py-6">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
            <div className="flex gap-4">
              <Link href="/privacy">
                <a className="hover:text-primary transition-colors">Privacy Policy</a>
              </Link>
              <Link href="/terms">
                <a className="hover:text-primary transition-colors">Terms of Use</a>
              </Link>
              <Link href="/">
                <a className="hover:text-primary transition-colors">Dashboard</a>
              </Link>
            </div>
            <div>
              © 2026 Tradeharbor.io. All rights reserved.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
